import { motion } from 'framer-motion';
import { ChevronDown, Camera, Image, Users, Calendar } from 'lucide-react';
import { useState } from 'react';
import { Modal } from './Modal';
import { MachhaCharacter } from '../../lib/machhaAsset';
import type { QuotaUsage } from '../../types';
import { clayStaggerContainer, clayStaggerItem } from '../../constants/animations';
import styles from './QuotaDetailModal.module.css';

function pct(used: number, limit: number): number {
  return limit > 0 ? Math.round((used / limit) * 100) : 0;
}

function barStyle(p: number): string {
  if (p >= 90) return styles['bar-rose'];
  if (p >= 60) return styles['bar-amber'];
  return styles['bar-mint'];
}

function ProgressBar({ used, limit, label }: { used: number; limit: number; label: string }) {
  const p = pct(used, limit);
  return (
    <div>
      <div className={styles['progress-header']}>
        <span className={styles['progress-label']}>{label}</span>
        <span className={styles['progress-value']}>{used}/{limit}</span>
      </div>
      <div className={styles['progress-track']}>
        <motion.div
          className={[styles['progress-fill'], barStyle(p)].join(' ')}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(p, 100)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

function EventRow({ detail }: { detail: QuotaUsage['eventDetails'][0] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles['event-row']}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={styles['event-button']}
      >
        <Calendar className={[styles['icon-sm'], styles['event-icon']].join(' ')} />
        <span className={styles['event-name']}>{detail.eventName}</span>
        <ChevronDown
          className={[styles['icon-sm'], styles['chevron'], open ? styles['chevron-open'] : ''].filter(Boolean).join(' ')}
        />
      </button>
      {open && (
        <div className={styles['event-details']}>
          <ProgressBar used={detail.photos.used} limit={detail.photos.limit} label="Photos" />
          <ProgressBar used={detail.albums.used} limit={detail.albums.limit} label="Albums" />
          <ProgressBar used={detail.ftpAccounts.used} limit={detail.ftpAccounts.limit} label="FTP Accounts" />
        </div>
      )}
    </div>
  );
}

interface QuotaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  quota: QuotaUsage | null;
}

export function QuotaDetailModal({ isOpen, onClose, quota }: QuotaDetailModalProps) {
  if (!quota) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Your Quota" size="md">
      <div className={styles['modal-body']}>
        {/* Overall events */}
        <div className={styles['events-card']}>
          <ProgressBar used={quota.events.used} limit={quota.events.limit} label="Events" />
        </div>

        {/* Per-event breakdown */}
        {quota.eventDetails.length > 0 ? (
          <div>
            <p className={styles['section-heading']}>
              Per-Event Usage
            </p>
            <motion.div className={styles['event-list']} variants={clayStaggerContainer} initial="hidden" animate="visible">
              {quota.eventDetails.map((d) => (
                <motion.div key={d.eventId} variants={clayStaggerItem}>
                  <EventRow detail={d} />
                </motion.div>
              ))}
            </motion.div>
          </div>
        ) : (
          <div className={styles['empty-state']}>
            <MachhaCharacter state="Empty" part="face" size={72} />
            <p className={styles['empty-text']}>No events yet. Create one to get started!</p>
          </div>
        )}

        {/* Limits summary */}
        <div className={styles['limits-card']}>
          <p className={styles['section-heading']}>
            Beta Limits
          </p>
          <div className={styles['limits-grid']}>
            <div className={styles['limit-item']}>
              <Calendar className={[styles['icon-sm'], styles['limit-icon-indigo']].join(' ')} />
              <span className={styles['limit-value']}>{quota.limits.maxEvents}</span>
              <span className={styles['limit-label']}>Events</span>
            </div>
            <div className={styles['limit-item']}>
              <Image className={[styles['icon-sm'], styles['limit-icon-orange']].join(' ')} />
              <span className={styles['limit-value']}>{quota.limits.maxPhotosPerEvent}</span>
              <span className={styles['limit-label']}>Photos/event</span>
            </div>
            <div className={styles['limit-item']}>
              <Users className={[styles['icon-sm'], styles['limit-icon-emerald']].join(' ')} />
              <span className={styles['limit-value']}>{quota.limits.maxAlbumsPerEvent}</span>
              <span className={styles['limit-label']}>Albums/event</span>
            </div>
            <div className={styles['limit-item']}>
              <Camera className={[styles['icon-sm'], styles['limit-icon-rose']].join(' ')} />
              <span className={styles['limit-value']}>{quota.limits.maxFtpAccountsPerEvent}</span>
              <span className={styles['limit-label']}>Cameras/event</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
